/** Projects repository: membership facts from /me merged with synced entity data. */
import type { ProjectMembership } from '@/lib/api/types';

import { db, type ProjectRecord } from './db';
import { listForEntity } from './outbox';

export const PROJECT_ENTITY = 'project';

function stringOr(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function nullableString(value: unknown, fallback: string | null): string | null {
  return typeof value === 'string' ? value : value === null ? null : fallback;
}

export async function listProjects(): Promise<ProjectRecord[]> {
  const rows = await db.projects.toArray();
  return rows.sort((a, b) => a.name.localeCompare(b.name));
}

export function getProject(id: string): Promise<ProjectRecord | undefined> {
  return db.projects.get(id);
}

/** Merge the /me project list: membership facts are authoritative from /me. */
export async function upsertFromMe(projects: ProjectMembership[]): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', db.projects, async () => {
    const memberOf = new Set(projects.map((project) => project.id));
    for (const project of projects) {
      const existing = await db.projects.get(project.id);
      await db.projects.put({
        id: project.id,
        organization_id: project.organization_id,
        name: project.name,
        code: project.code ?? null,
        status: project.status,
        cover_url: project.cover_url ?? null,
        membership: {
          party_role: project.party_role,
          role: project.role,
          permissions: project.permissions,
        },
        data: existing?.data ?? {},
        version: existing?.version ?? null,
        local_state: existing?.local_state ?? 'synced',
        updated_at: now,
      });
    }
    // Access removed on the server: forget synced projects no longer listed.
    // Locally created (pending) projects stay until the server confirms them.
    const stale = await db.projects
      .filter((row) => !memberOf.has(row.id) && row.local_state === 'synced')
      .primaryKeys();
    await db.projects.bulkDelete(stale);
  });
}

export async function applyProjectChange(
  entityId: string,
  op: 'upsert' | 'delete',
  version: number,
  data: Record<string, unknown> | null,
): Promise<void> {
  if (op === 'delete') {
    await db.projects.delete(entityId);
    return;
  }
  const existing = await db.projects.get(entityId);
  const incoming = data ?? {};
  // Local edits still queued win on screen; the server copy is recorded and the
  // queued mutation (carrying base_version) decides the outcome on push.
  const pendingLocal = (await listForEntity(PROJECT_ENTITY, entityId)).length > 0;
  const base: ProjectRecord = existing ?? {
    id: entityId,
    organization_id: stringOr(incoming['organization_id'], ''),
    name: stringOr(incoming['name'], ''),
    code: null,
    status: 'active',
    cover_url: null,
    membership: null,
    data: {},
    version: null,
    local_state: 'synced',
    updated_at: new Date().toISOString(),
  };
  await db.projects.put(
    pendingLocal
      ? { ...base, version }
      : {
          ...base,
          organization_id: stringOr(incoming['organization_id'], base.organization_id),
          name: stringOr(incoming['name'], base.name),
          code: nullableString(incoming['code'], base.code),
          status: stringOr(incoming['status'], base.status),
          cover_url: nullableString(incoming['cover_url'], base.cover_url),
          data: incoming,
          version,
          local_state: 'synced',
          updated_at: new Date().toISOString(),
        },
  );
}

export async function setProjectVersion(entityId: string, version: number): Promise<void> {
  const remaining = await listForEntity(PROJECT_ENTITY, entityId);
  await db.projects.update(entityId, {
    version,
    local_state: remaining.length ? 'pending' : 'synced',
  });
}
